<template>
  <div v-if="isExpandedInfo(props.groupRecommendation)">
    <Card class="information-card group-information">
      <template #content>
        <div class="icon">
          <span class="pi pi-info-circle" />
        </div>
        <div class="text">
          {{ props.groupRecommendation.detailDescription }}
        </div>
        <div class="close">
          <span class="pi pi-times-circle" />
        </div>
      </template>
    </Card>
  </div>
  <div
    v-if="isExpandedGroup(props.groupRecommendation)"
    class="recommendation-group-details"
  >
    <div class="already-upgraded-header-wrapper">
      <div class="already-upgraded-header">
        <span>{{ $t("recommendations.alreadyUpgraded") }}</span>
      </div>
    </div>
    <DataTable
      v-model:expanded-rows="expandedRows"
      :value="props.groupRecommendation.recommendations"
      data-key="id"
      responsive-layout="scroll"
      class="subtable"
    >
      <Column field="name">
        <template #body="subSlotProps">
          <div style="display: contents">
            <div :style="'width: calc(' + titleWidth + 'px - 6rem);'">
              <RecommendationSelectItem
                :parent-id="props.groupRecommendation.id"
                :recommendation="subSlotProps.data"
                :show-subtable-icons="showSubtableIcons"
                :expanded="isRecommendationExpandedInfo(subSlotProps.data)"
                :chip="getStepChipByRecommendationId(subSlotProps.data.id)"
                @toggle="() => expand(subSlotProps.data)"
              />
            </div>
          </div>
        </template>
      </Column>
      <Column
        v-if="showSubtableIcons"
        class="savings-col"
      >
        <template #body="slotProps">
          <RecommendationRating
            :rating="slotProps.data.info.saving"
            icon-type="savings"
          />
        </template>
      </Column>
      <Column
        v-if="showSubtableIcons"
        class="comfort-col"
      >
        <template #body="slotProps">
          <RecommendationRating
            :rating="slotProps.data.info.comfort"
            icon-type="comfort"
          />
        </template>
      </Column>
      <Column class="estimated-costs">
        <template #body="slotProps">
          <div class="amount-value">
            {{ formatNumberToString(slotProps.data.info.price) }}
          </div>
        </template>
      </Column>
      <Column class="already-upgraded">
        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.isUpgraded"
            :binary="true"
            :disabled="recommendationIsDisabled(slotProps.data.groupId)"
            @click="updateRecommendationUpgraded(slotProps.data)"
          />
        </template>
      </Column>

      <template #expansion="slotProps">
        <Card class="information-card">
          <template #content>
            <div class="icon">
              <span class="pi pi-info-circle" />
            </div>
            <div class="text">
              {{ slotProps.data.detailDescription }}
            </div>
            <div class="close">
              <span
                class="pi pi-times-circle"
                @click="expand(slotProps.data)"
              />
            </div>
          </template>
        </Card>
      </template>
    </DataTable>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { Recommendation, RecommendationGroup } from "@/types";
import { useRecommendations } from "@/store/recommendations";
import { useSettings } from "@/store/settings";
import { storeToRefs } from "pinia";
import {formatNumberToString} from "@/helpers";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import RecommendationRating from "@/components/result/recommendations/RecommendationRating.vue";
import RecommendationSelectItem from "@/components/result/recommendations/RecommendationSelectItem.vue";
import InputSwitch from "primevue/inputswitch";
import Card from "primevue/card";
import {usePassport} from "@/store";

export default defineComponent({
  name: "RecommendationGroupDetials",
  components: {
    DataTable,
    Column,
    RecommendationRating,
    RecommendationSelectItem,
    InputSwitch,
    Card,
  },
  props: {
    groupRecommendation: {
      type: Object as () => RecommendationGroup,
      required: true,
    },
  },
  setup(props) {
    const settingsStore = useSettings();
    const recommendationStore = useRecommendations();

    const { updateRecommendationUpgraded, isExpandedGroup, isExpandedInfo } =
      recommendationStore;
    const { recommendationGroups } = storeToRefs(recommendationStore);
    const { showSubtableIcons } =
      settingsStore.settings?.recommendationListSettings || {};
    const { steps } = storeToRefs(usePassport())

    const expandedRows = ref<Recommendation[]>([]);
    const titleWidth = ref<string | null>(null);

    onMounted(() => {
      handleResize();
      window.addEventListener("resize", handleResize);
    });

    const handleResize = () => {
      const element = document.getElementsByClassName(
        "recommended-measure-wrapper"
      )[0];
      if (element) {
        /*titleWidth.value = window.innerWidth > 960 ?
        `calc(${element.clientWidth}px - 6rem)` :  `calc(${element.clientWidth}px - 4rem)`;*/
        titleWidth.value = element.clientWidth + "";
      }
    };

    function expand(recommendation: Recommendation) {
      if (expandedRows.value.some((x) => x.id === recommendation.id)) {
        expandedRows.value = expandedRows.value.filter(
          (x) => x.id !== recommendation.id
        );
      } else {
        expandedRows.value = [...expandedRows.value, recommendation];
      }
    }

    function isRecommendationExpandedInfo(recommendation: Recommendation) {
      return expandedRows.value.find((r) => r.id === recommendation.id);
    }

    function recommendationIsDisabled(groupId: string) {
      const group = recommendationGroups.value.find((g) => g.id === groupId);
      return group ? !group.isSelected : true;
    }
    function getStepChipByRecommendationId(recommendationId: string): string | undefined {
      const step = steps.value.find(s =>
          s.items?.some(i => i.recommendations?.includes(recommendationId))
      )
      return step ? `Step ${step.step} · ${step.year}` : undefined
    }

    return {
      props,
      expandedRows,
      titleWidth,
      showSubtableIcons,
      recommendationGroups,
      recommendationIsDisabled,
      formatNumberToString,
      expand,
      updateRecommendationUpgraded,
      isExpandedGroup,
      isExpandedInfo,
      isRecommendationExpandedInfo,
      getStepChipByRecommendationId,
    };
  },
});
</script>

<style lang="scss" scoped>
.recommendation-group-details {
  padding: 0 0 0 4rem;

  .already-upgraded-header-wrapper {
    height: 3.5rem;
    display: flex;
    align-items: center;
    .already-upgraded-header {
      border-bottom: 1px solid;
      height: 0;
      width: 100%;
      align-items: center;
      justify-content: flex-end;
      display: flex;
    }
  }

  :deep(.p-datatable) {
    .estimated-costs {
      text-align: center;
      width: 12rem;
    }
    .already-upgraded {
      text-align: right;
      width: 7rem;
      .p-column-header-content {
        justify-content: flex-end;
      }
    }
  }
}
@media screen and (max-width: 810px) {
  .recommendation-group-details {
    padding: 0;

    :deep(.p-datatable) {
      .estimated-costs,
      .already-upgraded {
        width: auto;
      }
    }
  }
}
.information-card {
  border: 2px solid $primary-color;
  padding: 0.5rem 0.75rem;
  background-color: $primary-color-shadow-transparent;

  :deep(.p-card-content) {
    display: flex;
    align-content: center;
    justify-content: space-between;
    div {
      padding: 1rem;
      &.icon {
        display: flex;
        align-items: center;
        span {
          font-size: 1.75rem;
        }
      }
      &.close {
        position: relative;
        bottom: 0.3rem;
        left: 0.3rem;
        padding: 0.5rem;
        span {
          cursor: pointer;
        }
      }
    }

    .text {
      width: 20rem;
      flex: 1;
      text-align: justify;
    }
  }
}
.group-information {
  margin-bottom: 1rem;
}
</style>
